/**
 * Os cadastros das Configurações sobre o PostgREST — trilha T5a.
 *
 * Em arquivo próprio e espalhado no fim do `adaptadorSupabase`: são tabelas
 * novas (migração `0080`), sem nada em comum com as consultas antigas além do
 * cliente. Como no resto do adaptador, a LEITURA não filtra por clínica — quem
 * recorta é a RLS —, e a ESCRITA manda o `clinica_id`, que a política confere.
 */

import { supabase } from '@/supabase';
import { atual, pode } from '@/session/sessao';
import { situacaoDaFatura } from './plataformaDaVecchia';
import { COLUNAS_DA_COBRANCA, camposDaCobranca, paraInadimplencia } from './gateways';
import { normalizarGatilho } from './cadastros';
import { preferenciaPorChave } from './preferencias';
import type { PortaDeDados } from './porta';
import type {
    AssinaturaVistaPelaClinica, CargaDeAtendimentoECrm, CategoriaDeProcedimento, DadosDaEtiqueta, DadosDaFila,
    DadosDaMensagemRapida, ModoDeDistribuicao, DadosDoDepartamento, DadosDoPacoteDoCatalogo, EscopoDaMensagemRapida, PacoteDoCatalogo,
    PreferenciaSalva, ValorDePreferencia,
} from './cadastros';
import type { CicloDoPlano, Fatura, SituacaoDaClinica } from './tipos';

function cliente() {
    if (!supabase) throw new Error('Servidor não configurado.');
    return supabase;
}

function clinicaId(): string {
    const id = atual()?.clinicaId;
    if (!id) throw new Error('Sessão sem clínica: entre novamente.');
    return id;
}

function erro(mensagem: string, e: { message: string } | null): void {
    if (e) throw new Error(`${mensagem}: ${e.message}`);
}

function linhas(data: unknown): Record<string, unknown>[] {
    return (data ?? []) as Record<string, unknown>[];
}

/** O PostgREST devolve OBJETO quando a chave do embutido é única, e lista quando não é. */
function embutido(v: unknown): Record<string, unknown> | undefined {
    const x = Array.isArray(v) ? v[0] : v;
    return x && typeof x === 'object' ? (x as Record<string, unknown>) : undefined;
}

export type MetodosDosCadastros = Pick<PortaDeDados,
    | 'preferencias' | 'salvarPreferencias'
    | 'atendimentoECrm' | 'salvarDepartamento' | 'salvarFila' | 'salvarEtiqueta'
    | 'salvarMensagemRapida' | 'removerMensagemRapida' | 'usarMensagemRapida'
    | 'categoriasDeProcedimento' | 'salvarCategoriaDeProcedimento' | 'removerCategoriaDeProcedimento'
    | 'pacotesDoCatalogo' | 'salvarPacoteDoCatalogo'
    | 'assinaturaDaClinica'
>;

export const cadastrosNoSupabase: MetodosDosCadastros = {
    /* ------------------------------------------------------- preferências */

    async preferencias(): Promise<PreferenciaSalva[]> {
        const { data, error } = await cliente().from('preferencias_da_clinica').select('chave, valor');
        erro('Falha ao ler as preferências', error);
        return linhas(data).map((l) => ({ chave: String(l.chave), valor: l.valor as ValorDePreferencia }));
    },

    async salvarPreferencias(mudancas: { chave: string; valor: ValorDePreferencia | null }[]): Promise<PreferenciaSalva[]> {
        const desconhecida = mudancas.find((m) => !preferenciaPorChave(m.chave));
        if (desconhecida) throw new Error(`Preferência desconhecida: ${desconhecida.chave}.`);

        const clinica = clinicaId();
        const gravar = mudancas.filter((m) => m.valor !== null)
            .map((m) => ({ clinica_id: clinica, chave: m.chave, valor: m.valor }));
        /* Restaurar APAGA: o padrão mora no catálogo, e gravá-lo aqui o
           congelaria como se a clínica o tivesse escolhido. */
        const apagar = mudancas.filter((m) => m.valor === null).map((m) => m.chave);

        if (gravar.length) {
            const { error } = await cliente().from('preferencias_da_clinica').upsert(gravar, { onConflict: 'clinica_id,chave' });
            erro('Falha ao salvar as preferências', error);
        }
        if (apagar.length) {
            const { error } = await cliente().from('preferencias_da_clinica').delete().eq('clinica_id', clinica).in('chave', apagar);
            erro('Falha ao restaurar as preferências', error);
        }
        return this.preferencias();
    },

    /* -------------------------------------------------- atendimento e CRM */

    async atendimentoECrm(): Promise<CargaDeAtendimentoECrm> {
        const [depsResp, filasResp, vinculosResp, etiquetasResp, rapidasResp, membrosResp] = await Promise.all([
            cliente().from('departamentos').select('id, nome, descricao, ativo').order('nome'),
            cliente().from('filas_atendimento').select('id, nome, departamento_id, cor, sla_min, ativa, distribuicao').order('nome'),
            cliente().from('fila_membros').select('fila_id, membro_id'),
            cliente().from('etiquetas').select('id, nome, cor, departamento_id, fila_id, gatilhos, ativa').order('nome'),
            /* A RLS já tira as privadas dos outros e as restritas de quem não é
               gestão: a lista que chega é a que esta pessoa pode ver. */
            cliente().from('mensagens_rapidas').select('id, gatilho, titulo, texto, escopo, usos, ativa').order('gatilho'),
            cliente().from('membros').select('id, nome, acesso, arquivado_em').order('nome'),
        ]);
        erro('Falha ao ler os departamentos', depsResp.error);
        erro('Falha ao ler as filas', filasResp.error);
        erro('Falha ao ler quem atende cada fila', vinculosResp.error);
        erro('Falha ao ler as etiquetas', etiquetasResp.error);
        erro('Falha ao ler as mensagens rápidas', rapidasResp.error);
        erro('Falha ao ler a equipe', membrosResp.error);

        const nomes = new Map(linhas(membrosResp.data).map((m) => [String(m.id), String(m.nome ?? '')]));
        const vinculos = linhas(vinculosResp.data);

        const filas = linhas(filasResp.data).map((f) => ({
            id: String(f.id),
            nome: String(f.nome ?? ''),
            departamentoId: String(f.departamento_id),
            cor: String(f.cor ?? '#2986FF'),
            slaMin: Number(f.sla_min ?? 15),
            ativa: Boolean(f.ativa),
            distribuicao: (f.distribuicao as ModoDeDistribuicao | null) ?? 'manual',
            membros: vinculos.filter((v) => v.fila_id === f.id)
                .map((v) => ({ id: String(v.membro_id), nome: nomes.get(String(v.membro_id)) ?? '—' }))
                .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')),
        }));

        return {
            departamentos: linhas(depsResp.data).map((d) => {
                const dele = filas.filter((f) => f.departamentoId === d.id);
                return {
                    id: String(d.id),
                    nome: String(d.nome ?? ''),
                    descricao: (d.descricao as string | null) ?? '',
                    ativo: Boolean(d.ativo),
                    filas: dele.length,
                    usuarios: new Set(dele.flatMap((f) => f.membros.map((m) => m.id))).size,
                };
            }),
            filas,
            etiquetas: linhas(etiquetasResp.data).map((e) => ({
                id: String(e.id),
                nome: String(e.nome ?? ''),
                cor: String(e.cor ?? '#2986FF'),
                ...(e.departamento_id ? { departamentoId: String(e.departamento_id) } : {}),
                ...(e.fila_id ? { filaId: String(e.fila_id) } : {}),
                gatilhos: (e.gatilhos as string[] | null) ?? [],
                ativa: Boolean(e.ativa),
            })),
            mensagensRapidas: linhas(rapidasResp.data).map((m) => ({
                id: String(m.id),
                gatilho: String(m.gatilho ?? ''),
                titulo: String(m.titulo ?? ''),
                texto: String(m.texto ?? ''),
                escopo: m.escopo as EscopoDaMensagemRapida,
                usos: Number(m.usos ?? 0),
                ativa: Boolean(m.ativa),
            })),
            membros: linhas(membrosResp.data)
                .filter((m) => m.acesso === 'ativo' && !m.arquivado_em)
                .map((m) => ({ id: String(m.id), nome: String(m.nome ?? '') })),
            podeGestao: pode('configuracoes'),
        };
    },

    async salvarDepartamento(id: string | null, dados: DadosDoDepartamento): Promise<{ id: string }> {
        const nome = dados.nome.trim();
        if (!nome) throw new Error('O departamento precisa de um nome.');
        const linha = { nome, descricao: dados.descricao.trim() || null, ativo: dados.ativo };
        const resposta = id
            ? await cliente().from('departamentos').update(linha).eq('id', id).select('id').single()
            : await cliente().from('departamentos').insert({ clinica_id: clinicaId(), ...linha }).select('id').single();
        if (resposta.error?.code === '23505') throw new Error(`Já existe um departamento chamado ${nome}.`);
        erro('Falha ao salvar o departamento', resposta.error);
        return { id: String((resposta.data as { id: string }).id) };
    },

    async salvarFila(id: string | null, dados: DadosDaFila): Promise<{ id: string }> {
        const nome = dados.nome.trim();
        if (!nome) throw new Error('A fila precisa de um nome.');
        if (!dados.departamentoId) throw new Error('Escolha o departamento: fila sem departamento ninguém sabe quem atende.');
        const clinica = clinicaId();
        const linha = { nome, departamento_id: dados.departamentoId, cor: dados.cor, sla_min: dados.slaMin, ativa: dados.ativa, distribuicao: dados.distribuicao };
        const resposta = id
            ? await cliente().from('filas_atendimento').update(linha).eq('id', id).select('id').single()
            : await cliente().from('filas_atendimento').insert({ clinica_id: clinica, ...linha }).select('id').single();
        if (resposta.error?.code === '23505') throw new Error(`Já existe uma fila chamada ${nome}.`);
        if (resposta.error?.code === '23503') throw new Error('O departamento escolhido não existe nesta clínica.');
        erro('Falha ao salvar a fila', resposta.error);
        const filaId = String((resposta.data as { id: string }).id);

        /* Tira só quem saiu e põe só quem entrou. Apagar todos e reinserir
           deixaria, entre as duas chamadas, uma fila sem ninguém — e conversa
           que caísse nela nesse instante não teria quem a visse. */
        const atuaisResp = await cliente().from('fila_membros').select('membro_id').eq('fila_id', filaId);
        erro('Falha ao ler quem atende a fila', atuaisResp.error);
        const atuais = new Set(linhas(atuaisResp.data).map((l) => String(l.membro_id)));
        const novos = new Set(dados.membrosIds);
        const sairam = [...atuais].filter((m) => !novos.has(m));
        const entraram = [...novos].filter((m) => !atuais.has(m));

        if (entraram.length) {
            const { error } = await cliente().from('fila_membros')
                .insert(entraram.map((membro) => ({ clinica_id: clinica, fila_id: filaId, membro_id: membro })));
            erro('Falha ao vincular a equipe à fila', error);
        }
        if (sairam.length) {
            const { error } = await cliente().from('fila_membros').delete().eq('fila_id', filaId).in('membro_id', sairam);
            erro('Falha ao desvincular da fila', error);
        }
        return { id: filaId };
    },

    async salvarEtiqueta(id: string | null, dados: DadosDaEtiqueta): Promise<{ id: string }> {
        const nome = dados.nome.trim();
        if (!nome) throw new Error('A etiqueta precisa de um nome.');
        if (dados.filaId && !dados.departamentoId) throw new Error('Escolha o departamento antes da fila.');
        const gatilhos = [...new Set(dados.gatilhos.map((g) => g.trim()).filter(Boolean))];
        const linha = {
            nome, cor: dados.cor, gatilhos, ativa: dados.ativa,
            departamento_id: dados.departamentoId ?? null,
            fila_id: dados.filaId ?? null,
        };
        const resposta = id
            ? await cliente().from('etiquetas').update(linha).eq('id', id).select('id').single()
            : await cliente().from('etiquetas').insert({ clinica_id: clinicaId(), ...linha }).select('id').single();
        if (resposta.error?.code === '23505') throw new Error(`Já existe uma etiqueta chamada ${nome}.`);
        if (resposta.error?.code === '23503') throw new Error('A fila escolhida não pertence a esse departamento.');
        erro('Falha ao salvar a etiqueta', resposta.error);
        return { id: String((resposta.data as { id: string }).id) };
    },

    async salvarMensagemRapida(id: string | null, dados: DadosDaMensagemRapida): Promise<{ id: string }> {
        const gatilho = normalizarGatilho(dados.gatilho);
        if (!gatilho) throw new Error('A mensagem rápida precisa de um gatilho.');
        if (!dados.titulo.trim()) throw new Error('A mensagem rápida precisa de um título.');
        if (!dados.texto.trim()) throw new Error('Escreva o texto da mensagem.');
        /* O autor NÃO vai daqui: um gatilho do banco o tira da sessão. */
        const linha = { gatilho, titulo: dados.titulo.trim(), texto: dados.texto, escopo: dados.escopo, ativa: dados.ativa };
        const resposta = id
            ? await cliente().from('mensagens_rapidas').update(linha).eq('id', id).select('id').single()
            : await cliente().from('mensagens_rapidas').insert({ clinica_id: clinicaId(), ...linha }).select('id').single();
        if (resposta.error?.code === '23505') throw new Error(`Já existe a mensagem rápida /${gatilho}.`);
        if (resposta.error?.code === '42501') throw new Error('Só a gestão da clínica cria ou altera mensagem restrita.');
        erro('Falha ao salvar a mensagem rápida', resposta.error);
        return { id: String((resposta.data as { id: string }).id) };
    },

    async removerMensagemRapida(id: string): Promise<void> {
        const { error } = await cliente().from('mensagens_rapidas').delete().eq('id', id);
        erro('Falha ao remover a mensagem rápida', error);
    },

    async usarMensagemRapida(id: string): Promise<number> {
        const { data, error } = await cliente().rpc('usar_mensagem_rapida', { p_id: id });
        erro('Falha ao contar o uso da mensagem', error);
        return Number(data ?? 0);
    },

    /* ------------------------------------------ categorias e pacotes */

    async categoriasDeProcedimento(): Promise<CategoriaDeProcedimento[]> {
        const [categoriasResp, procedimentosResp] = await Promise.all([
            cliente().from('categorias_procedimento').select('id, nome, ativa').order('nome'),
            cliente().from('procedimentos').select('categoria'),
        ]);
        erro('Falha ao ler as categorias', categoriasResp.error);
        erro('Falha ao ler os procedimentos', procedimentosResp.error);
        const usos = new Map<string, number>();
        for (const p of linhas(procedimentosResp.data)) {
            const nome = String(p.categoria ?? '').trim();
            if (nome) usos.set(nome, (usos.get(nome) ?? 0) + 1);
        }
        return linhas(categoriasResp.data).map((c) => ({
            id: String(c.id),
            nome: String(c.nome ?? ''),
            ativa: Boolean(c.ativa),
            procedimentos: usos.get(String(c.nome ?? '')) ?? 0,
        }));
    },

    async salvarCategoriaDeProcedimento(id: string | null, dados: { nome: string; ativa: boolean }): Promise<{ id: string }> {
        const nome = dados.nome.trim();
        if (!nome) throw new Error('A categoria precisa de um nome.');
        if (!id) {
            const { data, error } = await cliente().from('categorias_procedimento')
                .insert({ clinica_id: clinicaId(), nome, ativa: dados.ativa }).select('id').single();
            if (error?.code === '23505') throw new Error(`Já existe uma categoria chamada ${nome}.`);
            erro('Falha ao criar a categoria', error);
            return { id: String((data as { id: string }).id) };
        }
        /* Renomear passa pela função: ela troca o texto dos procedimentos na
           mesma transação, e a categoria antiga não fica órfã. */
        const { error } = await cliente().rpc('salvar_categoria_de_procedimento', { p_id: id, p_nome: nome, p_ativa: dados.ativa });
        if (error?.code === '23505') throw new Error(`Já existe uma categoria chamada ${nome}.`);
        erro('Falha ao salvar a categoria', error);
        return { id };
    },

    async removerCategoriaDeProcedimento(id: string): Promise<{ removida: boolean }> {
        const { data, error } = await cliente().rpc('remover_categoria_de_procedimento', { p_id: id });
        erro('Falha ao remover a categoria', error);
        return { removida: data === true };
    },

    async pacotesDoCatalogo(): Promise<PacoteDoCatalogo[]> {
        const { data, error } = await cliente().from('pacotes_catalogo')
            .select('id, nome, procedimento_id, sessoes, preco_centavos, validade_dias, ativo').order('nome');
        erro('Falha ao ler os pacotes', error);
        return linhas(data).map((p) => ({
            id: String(p.id),
            nome: String(p.nome ?? ''),
            ...(p.procedimento_id ? { procedimentoId: String(p.procedimento_id) } : {}),
            ...(p.sessoes === null || p.sessoes === undefined ? {} : { sessoes: Number(p.sessoes) }),
            precoCentavos: Number(p.preco_centavos ?? 0),
            ...(p.validade_dias === null || p.validade_dias === undefined ? {} : { validadeDias: Number(p.validade_dias) }),
            ativo: Boolean(p.ativo),
        }));
    },

    async salvarPacoteDoCatalogo(id: string | null, dados: DadosDoPacoteDoCatalogo): Promise<{ id: string }> {
        const nome = dados.nome.trim();
        if (!nome) throw new Error('O pacote precisa de um nome.');
        if (!Number.isInteger(dados.precoCentavos) || dados.precoCentavos < 0) throw new Error('Informe o preço do pacote.');
        if (dados.sessoes !== undefined && (!Number.isInteger(dados.sessoes) || dados.sessoes <= 0)) {
            throw new Error('Sessões precisa ser um número inteiro maior que zero.');
        }
        const linha = {
            nome,
            procedimento_id: dados.procedimentoId ?? null,
            sessoes: dados.sessoes ?? null,
            preco_centavos: dados.precoCentavos,
            validade_dias: dados.validadeDias ?? null,
            ativo: dados.ativo,
        };
        const resposta = id
            ? await cliente().from('pacotes_catalogo').update(linha).eq('id', id).select('id').single()
            : await cliente().from('pacotes_catalogo').insert({ clinica_id: clinicaId(), ...linha }).select('id').single();
        if (resposta.error?.code === '23505') throw new Error(`Já existe um pacote chamado ${nome}.`);
        erro('Falha ao salvar o pacote', resposta.error);
        return { id: String((resposta.data as { id: string }).id) };
    },

    /* ------------------------------------------------------------ assinatura */

    async assinaturaDaClinica(): Promise<AssinaturaVistaPelaClinica> {
        const clinica = clinicaId();
        /* Aqui o filtro por clínica é necessário, e não enfeite: quem é membro
           de duas clínicas lê as assinaturas das duas, e esta tela fala da que
           está aberta. */
        const [clinicaResp, assinaturaResp, faturasResp, inadimplenciaResp] = await Promise.all([
            cliente().from('clinicas').select('nome, situacao, plano, teste_ate').eq('id', clinica).single(),
            cliente().from('assinaturas')
                .select('inicio, proxima_cobranca, cancelada_em, forma_de_pagamento, cartao_final, planos(nome, ciclo, preco_centavos, limite_membros, limite_unidades)')
                .eq('clinica_id', clinica).order('inicio', { ascending: false }).limit(1),
            cliente().from('faturas')
                .select(`id, clinica_id, competencia, vencimento, valor_centavos, pago_em, nota_fiscal, tipo, descricao, estornada_em, ${COLUNAS_DA_COBRANCA}`)
                .eq('clinica_id', clinica).order('competencia', { ascending: false }),
            cliente().rpc('minha_inadimplencia'),
        ]);
        erro('Falha ao ler a clínica', clinicaResp.error);
        erro('Falha ao ler a assinatura', assinaturaResp.error);
        erro('Falha ao ler as faturas', faturasResp.error);
        erro('Falha ao ler a inadimplência', inadimplenciaResp.error);
        const inadimplencia = paraInadimplencia(inadimplenciaResp.data);

        const c = clinicaResp.data as Record<string, unknown> | null;
        const a = linhas(assinaturaResp.data)[0];
        const plano = embutido(a?.planos);
        const nomeDaClinica = String(c?.nome ?? '');

        return {
            plano: String(plano?.nome ?? c?.plano ?? ''),
            ciclo: (plano?.ciclo as CicloDoPlano | undefined) ?? 'mensal',
            ...(plano?.preco_centavos === null || plano?.preco_centavos === undefined ? {} : { precoCentavos: Number(plano.preco_centavos) }),
            situacao: (c?.situacao as SituacaoDaClinica | undefined) ?? 'ativa',
            ...(a?.inicio ? { inicio: String(a.inicio) } : {}),
            ...(a?.proxima_cobranca ? { proximaCobranca: String(a.proxima_cobranca) } : {}),
            ...(a?.cancelada_em ? { canceladaEm: String(a.cancelada_em) } : {}),
            ...(plano?.limite_membros ? { limiteMembros: Number(plano.limite_membros) } : {}),
            ...(plano?.limite_unidades ? { limiteUnidades: Number(plano.limite_unidades) } : {}),
            faturas: linhas(faturasResp.data).map((f): Fatura => {
                const base = {
                    id: String(f.id),
                    clinicaId: String(f.clinica_id),
                    clinica: nomeDaClinica,
                    competencia: String(f.competencia ?? ''),
                    vencimento: String(f.vencimento ?? ''),
                    valorCentavos: Number(f.valor_centavos ?? 0),
                    ...(f.pago_em ? { pagoEm: String(f.pago_em) } : {}),
                    ...(f.nota_fiscal ? { notaFiscal: String(f.nota_fiscal) } : {}),
                    tipo: f.tipo === 'avulsa' ? 'avulsa' as const : 'assinatura' as const,
                    ...(f.descricao ? { descricao: String(f.descricao) } : {}),
                    ...(f.estornada_em ? { estornadaEm: String(f.estornada_em) } : {}),
                    ...camposDaCobranca(f),
                };
                return { ...base, situacao: situacaoDaFatura(base) };
            }),
            ...(inadimplencia ? { inadimplencia } : {}),
        };
    },
};
